import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import useSeo from "@/hooks/useSeo";
import {
  ArrowLeft, ArrowRight, ArrowUpRight, Clock, Calendar, ChevronRight,
  Info, AlertTriangle, Sparkles, Twitter, Linkedin, Copy, Check,
  BookOpen, Quote, Lightbulb, ExternalLink, CheckCircle2,
} from "lucide-react";
import { getPostBySlug, getRelated, getHeroVisual, BLOG_POSTS } from "@/data/blogPosts";
import PostHeroVisual from "@/components/PostHeroVisual";

const DASABO_LOGO = "/logo.png";
const SITE_URL = "https://autofattura.dasabo.com";

// ---- JSON-LD injector --------------------------------------------------

function useArticleJsonLd(post) {
  React.useEffect(() => {
    if (!post) return undefined;
    const ids = [];
    const article = document.createElement("script");
    article.type = "application/ld+json";
    article.id = "jsonld-article";
    article.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Article",
      headline: post.title,
      description: post.description,
      keywords: (post.keywords || []).join(", "),
      author: { "@type": "Organization", name: "DASABO SRL", url: SITE_URL },
      publisher: {
        "@type": "Organization",
        name: "DASABO SRL",
        logo: { "@type": "ImageObject", url: `${SITE_URL}/logo.png` },
      },
      datePublished: post.publishedAt,
      dateModified: post.updatedAt || post.publishedAt,
      mainEntityOfPage: `${SITE_URL}/blog/${post.slug}`,
      inLanguage: "it",
    });
    document.head.appendChild(article);
    ids.push(article.id);

    const bc = document.createElement("script");
    bc.type = "application/ld+json";
    bc.id = "jsonld-breadcrumb";
    bc.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
        { "@type": "ListItem", position: 2, name: "Blog", item: `${SITE_URL}/blog` },
        { "@type": "ListItem", position: 3, name: post.title, item: `${SITE_URL}/blog/${post.slug}` },
      ],
    });
    document.head.appendChild(bc);
    ids.push(bc.id);

    if (post.faq?.length) {
      const faq = document.createElement("script");
      faq.type = "application/ld+json";
      faq.id = "jsonld-faq";
      faq.text = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: post.faq.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      });
      document.head.appendChild(faq);
      ids.push(faq.id);
    }
    return () => ids.forEach((id) => document.getElementById(id)?.remove());
  }, [post]);
}

// ---- Reading progress + active heading ---------------------------------

function useReadingProgress() {
  const [progress, setProgress] = React.useState(0);
  React.useEffect(() => {
    const handler = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const pct = docHeight > 0 ? Math.min(100, Math.max(0, (scrollTop / docHeight) * 100)) : 0;
      setProgress(pct);
    };
    handler();
    window.addEventListener("scroll", handler, { passive: true });
    window.addEventListener("resize", handler);
    return () => {
      window.removeEventListener("scroll", handler);
      window.removeEventListener("resize", handler);
    };
  }, []);
  return progress;
}

function useActiveHeading(ids) {
  const [activeId, setActiveId] = React.useState(ids[0] || null);
  const key = ids.join(",");
  React.useEffect(() => {
    if (!ids.length) return undefined;
    const visibility = new Map();
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => visibility.set(e.target.id, e.isIntersecting ? e.intersectionRatio : 0));
        const visible = [...visibility.entries()]
          .filter(([, r]) => r > 0)
          .sort((a, b) => b[1] - a[1]);
        if (visible.length) setActiveId(visible[0][0]);
      },
      { rootMargin: "-100px 0px -60% 0px", threshold: [0, 0.25, 0.5, 0.75, 1] },
    );
    const observed = [];
    ids.forEach((id) => {
      const el = document.getElementById(id);
      if (el) {
        observer.observe(el);
        observed.push(el);
      }
    });
    return () => observed.forEach((el) => observer.unobserve(el));
  }, [key]);
  return activeId;
}

// ---- Main component ---------------------------------------------------

export default function BlogPost() {
  const { slug } = useParams();
  const post = getPostBySlug(slug);

  useSeo({
    title: post?.title || "Articolo non trovato",
    description: post?.description || "",
    path: `/blog/${slug}`,
    noindex: !post,
  });
  useArticleJsonLd(post);
  const progress = useReadingProgress();
  const tocIds = React.useMemo(() => (post?.toc || []).map((t) => t.id), [post]);
  const activeId = useActiveHeading(tocIds);

  if (!post) return <Navigate to="/blog" replace />;

  const related = getRelated(slug);
  const shareUrl = `${SITE_URL}/blog/${slug}`;

  return (
    <div className="min-h-screen bg-[#FAF9F6]" data-testid="blog-post-page">
      {/* Reading progress */}
      <div className="fixed top-0 left-0 right-0 h-[3px] z-[60] pointer-events-none">
        <div
          data-testid="blog-post-reading-progress"
          className="h-full bg-gradient-to-r from-[#1D4ED8] via-[#7C3AED] to-[#F97316] transition-[width] duration-75"
          style={{ width: `${progress}%` }}
        />
      </div>

      {/* Top nav */}
      <header className="sticky top-0 z-30 bg-[#FAF9F6]/85 backdrop-blur-md border-b border-[#E8E4D9]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/blog" className="flex items-center gap-2 text-sm font-semibold text-[#0A0A0A] hover:text-[#1D4ED8] transition-colors" data-testid="blog-post-back">
            <ArrowLeft className="w-4 h-4" /> Tutti gli articoli
          </Link>
          <div className="flex items-center gap-6 text-sm font-medium text-[#4A4A4A]">
            <Link to="/docs" className="hover:text-[#0A0A0A] hidden sm:inline">Documentazione</Link>
            <Link to="/register" className="px-4 py-1.5 rounded-full bg-[#0A0A0A] text-white hover:bg-[#1D4ED8] transition-colors">Prova gratis</Link>
          </div>
        </div>
      </header>

      {/* Breadcrumb */}
      <nav aria-label="Breadcrumb" className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 text-xs text-[#737373]">
        <ol className="flex items-center gap-1.5 flex-wrap">
          <li><Link to="/" className="hover:text-[#0A0A0A]">Home</Link></li>
          <li><ChevronRight className="w-3 h-3 inline" /></li>
          <li><Link to="/blog" className="hover:text-[#0A0A0A]">Blog</Link></li>
          <li><ChevronRight className="w-3 h-3 inline" /></li>
          <li className="text-[#0A0A0A] font-medium truncate max-w-xs">{post.title}</li>
        </ol>
      </nav>

      {/* Hero */}
      <section className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-12">
        <div
          aria-hidden="true"
          className="absolute -top-4 right-0 sm:right-4 lg:-right-10 font-display font-black text-[180px] sm:text-[220px] lg:text-[260px] text-[#0A0A0A]/[0.04] leading-none tracking-tighter pointer-events-none select-none"
        >
          {getPostNumber(slug)}
        </div>
        <div className="relative z-10">
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[#1D4ED8] font-bold mb-5">
            <BookOpen className="w-3.5 h-3.5" />
            <span>{post.hero?.kicker || post.category}</span>
          </div>
          <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter text-[#0A0A0A] leading-[1.02] max-w-3xl">
            {post.hero?.title || post.title}
          </h1>
          {post.hero?.subtitle && (
            <p className="mt-7 text-lg sm:text-xl text-[#4A4A4A] leading-[1.55] max-w-3xl font-light">
              {post.hero.subtitle}
            </p>
          )}
          <div className="mt-8 flex items-center gap-4 flex-wrap">
            <AuthorAvatar />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-[#0A0A0A]">Redazione Autofattura by Dasabo</div>
              <div className="text-xs text-[#737373] flex items-center gap-2 mt-0.5 flex-wrap">
                <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> <time dateTime={post.updatedAt || post.publishedAt}>Aggiornato il {formatDate(post.updatedAt || post.publishedAt)}</time></span>
                <span>·</span>
                <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {post.readingMinutes} min di lettura</span>
                <span>·</span>
                <span className="font-semibold text-[#1D4ED8]">{post.category}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Divider */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="h-px bg-gradient-to-r from-transparent via-[#0A0A0A]/15 to-transparent" />
      </div>

      {/* Hero visual banner */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-10">
        <div className="rounded-2xl overflow-hidden shadow-lg" data-testid="blog-post-hero-visual">
          <PostHeroVisual
            visual={getHeroVisual(slug)}
            aspect="aspect-[21/8]"
            size="large"
            showBadge={false}
          />
        </div>
      </div>

      {/* TL;DR summary box */}
      {post.tldr?.length > 0 && (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-10">
          <TldrBox items={post.tldr} />
        </div>
      )}

      {/* Article body + sticky TOC */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-16 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_240px] gap-10 lg:gap-16">
        <article className="max-w-3xl">
          {post.body.map((block, i) => renderBlock(block, i, post.body))}

          <ShareBar url={shareUrl} title={post.title} />

          {post.faq?.length > 0 && (
            <div className="mt-16">
              <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[#1D4ED8] font-bold mb-3">
                <span className="w-4 h-px bg-[#1D4ED8]" /> Domande frequenti
              </div>
              <h2 className="font-display font-black text-3xl tracking-tighter text-[#0A0A0A] mb-6">
                Le domande che ci fate su questo argomento
              </h2>
              <div className="space-y-3">
                {post.faq.map((f, i) => (
                  <details
                    key={i}
                    data-testid={`blog-faq-${i}`}
                    className="group bg-white border border-[#E8E4D9] rounded-2xl p-5 hover:border-[#1D4ED8]/40 transition-colors"
                  >
                    <summary className="cursor-pointer font-semibold text-[#0A0A0A] flex items-center justify-between list-none gap-4">
                      <span className="flex items-center gap-3">
                        <span className="w-6 h-6 rounded-full bg-[#1D4ED8]/10 text-[#1D4ED8] text-xs font-bold grid place-items-center flex-shrink-0">
                          {String(i + 1).padStart(2, "0")}
                        </span>
                        {f.q}
                      </span>
                      <ChevronRight className="w-4 h-4 text-[#737373] group-open:rotate-90 transition-transform flex-shrink-0" />
                    </summary>
                    <p className="mt-3 ml-9 text-[#4A4A4A] leading-relaxed">{f.a}</p>
                  </details>
                ))}
              </div>
            </div>
          )}

          {/* Author bio card */}
          <AuthorCard />
        </article>

        {/* TOC sidebar */}
        {post.toc?.length > 0 && (
          <aside className="hidden lg:block sticky top-24 self-start" data-testid="blog-post-toc">
            <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[#1D4ED8] font-bold mb-4">
              <span className="w-4 h-px bg-[#1D4ED8]" /> In questo articolo
            </div>
            <nav className="space-y-0.5 border-l border-[#E8E4D9] pl-0 max-h-[70vh] overflow-y-auto">
              {post.toc.map(({ id, label }) => {
                const isActive = activeId === id;
                return (
                  <a
                    key={id}
                    href={`#${id}`}
                    data-testid={`toc-link-${id}`}
                    className={`group flex items-start gap-3 px-4 py-2 text-sm transition-all border-l-2 -ml-px ${
                      isActive
                        ? "border-[#1D4ED8] text-[#0A0A0A] font-semibold"
                        : "border-transparent text-[#737373] hover:text-[#0A0A0A] hover:border-[#0A0A0A]/30"
                    }`}
                  >
                    <span className={`mt-2 w-1 h-1 rounded-full transition-colors ${isActive ? "bg-[#1D4ED8]" : "bg-[#737373]/50 group-hover:bg-[#0A0A0A]"}`} />
                    <span className="leading-snug">{label}</span>
                  </a>
                );
              })}
            </nav>

            <div className="mt-10 p-5 rounded-2xl bg-[#0A0A0A] text-white">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#F97316] font-bold mb-2">Prova subito</div>
              <div className="font-display font-bold text-lg tracking-tight leading-tight mb-3">
                5 crediti gratis. Senza carta.
              </div>
              <Link
                to="/register"
                data-testid="blog-toc-cta"
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-white hover:text-[#F97316] transition-colors"
              >
                Registrati <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </aside>
        )}
      </section>

      {/* Related posts */}
      {related.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20 border-t border-[#E8E4D9] pt-16">
          <div className="flex items-baseline justify-between mb-8 flex-wrap gap-3">
            <div>
              <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[#1D4ED8] font-bold mb-2">
                <span className="w-4 h-px bg-[#1D4ED8]" /> Continua a leggere
              </div>
              <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tighter text-[#0A0A0A]">
                Guide collegate.
              </h2>
            </div>
            <Link
              to="/blog"
              data-testid="blog-post-see-all"
              className="text-sm font-semibold text-[#0A0A0A] hover:text-[#1D4ED8] transition-colors inline-flex items-center gap-1.5"
            >
              Tutti gli articoli <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {related.map((r, idx) => (
              <Link
                key={r.slug}
                to={`/blog/${r.slug}`}
                data-testid={`blog-related-${r.slug}`}
                className="group relative bg-white rounded-2xl border border-[#E8E4D9] overflow-hidden hover:border-[#0A0A0A] hover:shadow-xl hover:-translate-y-1 transition-all"
              >
                <PostHeroVisual visual={getHeroVisual(r.slug)} readingMinutes={r.readingMinutes} aspect="aspect-[16/9]" />
                <div className="p-5 relative">
                  <div className="absolute top-2 right-4 font-display font-black text-4xl text-[#0A0A0A]/5 leading-none pointer-events-none">
                    {String(idx + 1).padStart(2, "0")}
                  </div>
                  <div className="relative">
                    <div className="inline-block text-[10px] uppercase tracking-[0.2em] text-[#1D4ED8] font-bold mb-3">
                      {r.category}
                    </div>
                    <h4 className="font-display font-bold text-lg tracking-tight text-[#0A0A0A] group-hover:text-[#1D4ED8] transition-colors leading-tight line-clamp-3">
                      {r.title}
                    </h4>
                    <div className="mt-4 flex items-center justify-between text-xs text-[#737373]">
                      <span className="flex items-center gap-1.5"><Clock className="w-3 h-3" /> {r.readingMinutes} min</span>
                      <ArrowRight className="w-4 h-4 text-[#0A0A0A] group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Final CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <div className="rounded-3xl bg-[#0A0A0A] text-white p-8 sm:p-12 lg:p-16 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-[#1D4ED8]/25 via-transparent to-[#F97316]/10" />
          <div className="relative z-10 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
            <div>
              <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[#F97316] font-bold mb-3">
                <Sparkles className="w-3.5 h-3.5" /> <span>Passa dalla teoria alla pratica</span>
              </div>
              <h3 className="font-display font-black text-3xl sm:text-4xl tracking-tighter mb-3">
                Ora provalo davvero.
              </h3>
              <p className="text-white/70 max-w-xl leading-relaxed">
                5 crediti gratuiti al signup. Carta di credito non richiesta. Se non ti piace, esci senza pagare nulla.
              </p>
            </div>
            <Link
              to="/register"
              data-testid="blog-post-cta-register"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white text-[#0A0A0A] font-semibold text-sm hover:bg-[#F97316] hover:text-white transition-colors whitespace-nowrap"
            >
              Prova gratis <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <footer className="bg-[#0A0A0A] text-[#737373] py-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 flex items-center justify-between flex-wrap gap-4 text-xs">
          <div className="flex items-center gap-2 text-white font-display font-extrabold text-base">
            Autofattura by
            <img src={DASABO_LOGO} alt="Dasabo" className="h-5 w-auto brightness-0 invert" />
          </div>
          <div>© 2026 DASABO SRL · P.IVA IT07407380828 · Palermo</div>
        </div>
      </footer>
    </div>
  );
}

// ---- Body renderer -----------------------------------------------------

function renderBlock(block, idx, allBlocks) {
  const isFirstParagraph = block.type === "p" && idx === firstParagraphIndex(allBlocks);
  switch (block.type) {
    case "p":
      return (
        <p
          key={idx}
          className={`text-[#0A0A0A]/85 mb-5 ${
            isFirstParagraph
              ? "text-[19px] sm:text-[20px] leading-[1.65] first-letter:float-left first-letter:font-display first-letter:font-black first-letter:text-[72px] first-letter:leading-[0.85] first-letter:mr-2.5 first-letter:mt-1 first-letter:text-[#1D4ED8]"
              : "text-[17px] leading-[1.75]"
          }`}
        >
          {block.text}
        </p>
      );
    case "h2":
      return (
        <h2
          key={idx}
          id={block.id}
          className="relative font-display font-black text-3xl sm:text-4xl tracking-tighter text-[#0A0A0A] mt-16 mb-6 scroll-mt-24 pl-5 before:content-[''] before:absolute before:left-0 before:top-2 before:bottom-2 before:w-1 before:bg-gradient-to-b before:from-[#1D4ED8] before:to-[#7C3AED] before:rounded-full"
        >
          {block.text}
        </h2>
      );
    case "h3":
      return (
        <h3
          key={idx}
          id={block.id}
          className="font-display font-bold text-xl sm:text-2xl tracking-tight text-[#0A0A0A] mt-10 mb-3 scroll-mt-24"
        >
          {block.text}
        </h3>
      );
    case "ul":
      return (
        <ul key={idx} className="mb-6 space-y-2.5">
          {block.items.map((it, i) => (
            <li key={i} className="pl-6 relative text-[17px] text-[#0A0A0A]/85 leading-[1.7]">
              <span className="absolute left-0 top-[11px] w-2 h-2 rounded-full bg-gradient-to-br from-[#1D4ED8] to-[#7C3AED]" />
              {it}
            </li>
          ))}
        </ul>
      );
    case "ol":
      return (
        <ol key={idx} className="mb-6 space-y-3">
          {block.items.map((it, i) => (
            <li key={i} className="pl-12 relative text-[17px] text-[#0A0A0A]/85 leading-[1.7] pt-1">
              <span className="absolute left-0 top-0 w-9 h-9 rounded-full bg-[#0A0A0A] text-white text-xs font-bold grid place-items-center shadow-sm">
                {String(i + 1).padStart(2, "0")}
              </span>
              {it}
            </li>
          ))}
        </ol>
      );
    case "callout": {
      const conf = {
        blue: { bg: "bg-blue-50", border: "border-blue-200", text: "text-blue-900", accent: "text-blue-600", Icon: Info },
        coral: { bg: "bg-orange-50", border: "border-orange-200", text: "text-orange-900", accent: "text-orange-600", Icon: AlertTriangle },
        amber: { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-900", accent: "text-amber-700", Icon: Sparkles },
      };
      const c = conf[block.variant] || conf.blue;
      const Icon = c.Icon;
      return (
        <aside key={idx} className={`my-8 p-5 rounded-2xl border ${c.bg} ${c.border} ${c.text} flex gap-4`}>
          <div className={`flex-shrink-0 w-10 h-10 rounded-xl grid place-items-center bg-white ${c.accent} shadow-sm`}>
            <Icon className="w-5 h-5" />
          </div>
          <div>
            {block.title && <div className="font-bold text-sm uppercase tracking-wider mb-1.5">{block.title}</div>}
            <div className="text-[15px] leading-relaxed">{block.text}</div>
          </div>
        </aside>
      );
    }
    case "example":
      return (
        <div key={idx} className="my-10 rounded-2xl border-2 border-[#0A0A0A]/10 bg-white overflow-hidden shadow-sm">
          <div className="bg-[#0A0A0A] text-white px-5 py-3 flex items-center gap-2">
            <Lightbulb className="w-4 h-4 text-[#F97316]" />
            <span className="text-xs uppercase tracking-[0.2em] font-bold">Esempio pratico</span>
            {block.subject && <span className="text-xs text-white/60 ml-auto">{block.subject}</span>}
          </div>
          <div className="p-6">
            {block.title && <div className="font-display font-bold text-xl text-[#0A0A0A] mb-3 tracking-tight">{block.title}</div>}
            <p className="text-[16px] text-[#0A0A0A]/85 leading-[1.65] mb-4">{block.body}</p>
            {block.breakdown?.length > 0 && (
              <div className="rounded-xl bg-[#FAF9F6] border border-[#E8E4D9] divide-y divide-[#E8E4D9]">
                {block.breakdown.map((row, i) => (
                  <div key={i} className="flex items-center justify-between px-4 py-2.5 text-sm">
                    <span className="text-[#4A4A4A]">{row.label}</span>
                    <span className={`font-mono ${row.emphasis ? "font-bold text-[#0A0A0A]" : "text-[#0A0A0A]"}`}>{row.value}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      );
    case "stats":
      return (
        <div key={idx} className={`my-10 grid gap-4 grid-cols-${Math.min(4, block.items.length)}`} style={{ gridTemplateColumns: `repeat(${Math.min(block.items.length, 4)}, minmax(0,1fr))` }}>
          {block.items.map((s, i) => (
            <div key={i} className="rounded-2xl bg-white border border-[#E8E4D9] p-5 hover:border-[#1D4ED8]/40 transition-colors">
              <div className="font-display font-black text-3xl sm:text-4xl tracking-tighter text-[#0A0A0A] leading-none">{s.value}</div>
              <div className="text-xs uppercase tracking-wider text-[#737373] font-semibold mt-2">{s.label}</div>
              {s.hint && <div className="text-xs text-[#4A4A4A] mt-2 leading-relaxed">{s.hint}</div>}
            </div>
          ))}
        </div>
      );
    case "quote":
      return (
        <blockquote key={idx} className="my-10 relative pl-16 pr-5 py-6 rounded-2xl bg-gradient-to-br from-[#1D4ED8]/5 to-[#7C3AED]/5 border-l-4 border-[#1D4ED8]">
          <Quote className="absolute top-5 left-4 w-8 h-8 text-[#1D4ED8]/30" />
          <p className="font-display text-xl sm:text-2xl italic tracking-tight text-[#0A0A0A] leading-snug">
            {block.text}
          </p>
          {block.cite && (
            <footer className="mt-3 text-sm text-[#4A4A4A] font-semibold">— {block.cite}</footer>
          )}
        </blockquote>
      );
    case "code":
      return (
        <figure key={idx} className="my-8">
          {block.caption && (
            <figcaption className="text-xs uppercase tracking-[0.2em] font-bold text-[#737373] mb-2">
              {block.caption}
            </figcaption>
          )}
          <pre className="rounded-2xl bg-[#0A0A0A] text-[#F5F5F5] p-5 overflow-x-auto text-[13px] leading-relaxed border border-[#0A0A0A] font-mono">
            <code>{block.text}</code>
          </pre>
        </figure>
      );
    case "table":
      return (
        <div key={idx} className="my-8 overflow-x-auto rounded-2xl border border-[#E8E4D9] bg-white shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-[#0A0A0A] text-white">
              <tr>
                {block.cols.map((c, i) => (
                  <th key={i} className="text-left px-4 py-3 font-semibold text-xs uppercase tracking-wider">{c}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E8E4D9]">
              {block.rows.map((row, i) => (
                <tr key={i} className={`${i % 2 === 0 ? "bg-white" : "bg-[#FAF9F6]"} hover:bg-[#1D4ED8]/5 transition-colors`}>
                  {row.map((cell, j) => (
                    <td key={j} className={`px-4 py-3 text-[#0A0A0A]/85 ${j === 0 ? "font-medium text-[#0A0A0A]" : ""}`}>{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    case "sources":
      return (
        <div key={idx} className="mt-14 pt-8 border-t border-[#E8E4D9]">
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[#737373] font-bold mb-4">
            <span className="w-4 h-px bg-[#737373]" /> Fonti e riferimenti
          </div>
          <ol className="space-y-2 text-sm text-[#4A4A4A]">
            {block.items.map((it, i) => (
              <li key={i} className="pl-9 relative leading-relaxed">
                <span className="absolute left-0 top-0 w-6 h-6 rounded-full bg-[#0A0A0A]/5 text-[#737373] text-[10px] font-bold grid place-items-center">
                  {i + 1}
                </span>
                {it.href ? (
                  <a
                    href={it.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#1D4ED8] hover:underline inline-flex items-center gap-1"
                  >
                    {it.text} <ExternalLink className="w-3 h-3" />
                  </a>
                ) : (
                  <span>{it.text}</span>
                )}
              </li>
            ))}
          </ol>
        </div>
      );
    case "cta": {
      const dark = block.variant === "dark";
      return (
        <div
          key={idx}
          className={`my-10 p-6 rounded-2xl relative overflow-hidden ${
            dark ? "bg-[#0A0A0A] text-white" : "bg-white border border-[#E8E4D9]"
          } flex items-center justify-between gap-4 flex-wrap`}
        >
          {dark && <div className="absolute inset-0 bg-gradient-to-br from-[#1D4ED8]/20 via-transparent to-[#F97316]/10" />}
          <div className="relative z-10 flex items-center gap-3">
            <Sparkles className={`w-5 h-5 ${dark ? "text-[#F97316]" : "text-[#1D4ED8]"} flex-shrink-0`} />
            <span className={`font-semibold ${dark ? "text-white" : "text-[#0A0A0A]"}`}>{block.label}</span>
          </div>
          <Link
            to={block.href}
            data-testid={`blog-cta-inline-${idx}`}
            className={`relative z-10 inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold whitespace-nowrap transition-colors ${
              dark ? "bg-white text-[#0A0A0A] hover:bg-[#F97316] hover:text-white" : "bg-[#0A0A0A] text-white hover:bg-[#1D4ED8]"
            }`}
          >
            Vai <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      );
    }
    default:
      return null;
  }
}

// ---- Reusable components -----------------------------------------------

function TldrBox({ items }) {
  return (
    <div className="rounded-2xl bg-[#0A0A0A] text-white p-6 sm:p-8 relative overflow-hidden" data-testid="blog-post-tldr">
      <div className="absolute inset-0 bg-gradient-to-br from-[#1D4ED8]/20 via-transparent to-[#F97316]/8 pointer-events-none" />
      <div className="relative">
        <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[#F97316] font-bold mb-4">
          <Sparkles className="w-3.5 h-3.5" /> In sintesi
        </div>
        <ul className="space-y-3">
          {items.map((it, i) => (
            <li key={i} className="flex items-start gap-3 text-[15px] text-white/90 leading-relaxed">
              <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
              <span>{it}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function ShareBar({ url, title }) {
  const [copied, setCopied] = React.useState(false);
  const encoded = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* silent fallback */ }
  };
  return (
    <div className="mt-14 pt-8 border-t border-[#E8E4D9] flex items-center justify-between flex-wrap gap-4" data-testid="blog-share-bar">
      <div className="text-xs uppercase tracking-[0.2em] text-[#737373] font-semibold">Condividi questa guida</div>
      <div className="flex items-center gap-2">
        <a
          href={`https://twitter.com/intent/tweet?text=${encodedTitle}&url=${encoded}`}
          target="_blank" rel="noopener noreferrer" data-testid="share-twitter"
          className="w-10 h-10 rounded-full bg-white border border-[#E8E4D9] grid place-items-center text-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white hover:border-[#0A0A0A] transition-colors"
          aria-label="Condividi su X"
        ><Twitter className="w-4 h-4" /></a>
        <a
          href={`https://www.linkedin.com/sharing/share-offsite/?url=${encoded}`}
          target="_blank" rel="noopener noreferrer" data-testid="share-linkedin"
          className="w-10 h-10 rounded-full bg-white border border-[#E8E4D9] grid place-items-center text-[#0A0A0A] hover:bg-[#0A66C2] hover:text-white hover:border-[#0A66C2] transition-colors"
          aria-label="Condividi su LinkedIn"
        ><Linkedin className="w-4 h-4" /></a>
        <button
          type="button" onClick={onCopy} data-testid="share-copy"
          className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border text-sm font-semibold transition-all ${
            copied ? "bg-emerald-50 border-emerald-200 text-emerald-700"
              : "bg-white border-[#E8E4D9] text-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white hover:border-[#0A0A0A]"
          }`}
          aria-label="Copia link"
        >
          {copied ? <><Check className="w-4 h-4" /> Copiato</> : <><Copy className="w-4 h-4" /> Copia link</>}
        </button>
      </div>
    </div>
  );
}

function AuthorAvatar() {
  return (
    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#1D4ED8] via-[#7C3AED] to-[#F97316] grid place-items-center text-white font-display font-black text-sm flex-shrink-0 shadow-md">
      D
    </div>
  );
}

function AuthorCard() {
  return (
    <div className="mt-16 p-6 sm:p-7 rounded-2xl bg-white border border-[#E8E4D9] flex items-start gap-5" data-testid="blog-author-card">
      <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-gradient-to-br from-[#1D4ED8] via-[#7C3AED] to-[#F97316] grid place-items-center text-white font-display font-black text-lg flex-shrink-0 shadow-md">
        D
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-xs uppercase tracking-[0.2em] text-[#737373] font-bold mb-1">A cura di</div>
        <div className="font-display font-bold text-lg text-[#0A0A0A]">Redazione Autofattura by Dasabo</div>
        <p className="text-sm text-[#4A4A4A] leading-relaxed mt-2">
          La redazione di DASABO SRL è composta da fiscalisti, sviluppatori e imprenditori palermitani. Scriviamo di
          autofatture estere perché le facciamo davvero (e prima non le sopportavamo). Sede legale: Via Imperatore
          Federico 100, Palermo. Contatti: <a href="mailto:supporto@dasabo.com" className="text-[#1D4ED8] hover:underline">supporto@dasabo.com</a>.
        </p>
      </div>
    </div>
  );
}

// ---- Helpers ------------------------------------------------------------

function firstParagraphIndex(blocks) {
  return blocks.findIndex((b) => b.type === "p");
}

function getPostNumber(slug) {
  const idx = BLOG_POSTS.findIndex((p) => p.slug === slug);
  return idx >= 0 ? String(idx + 1).padStart(2, "0") : "01";
}

function formatDate(iso) {
  const d = new Date(iso);
  return d.toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" });
}
